import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback } from 'react';

import { defaultNS, useTranslation } from '@/lib/i18n';

import { AlertPage } from '../-shared/AlertPage';
import { createSearchSchema } from '../-shared/search-schema';
import type { AlertRowWithUser, AlertSearchParams } from '../-shared/types';
import {
  createAlertExportHook,
  createAlertQueryHook,
} from '../-shared/useAlertQuery';
import { SoftwareAlertType } from '../../-zod-schema';

import {
  ColumnKey,
  SortableColumnKey,
  useSoftwareAlertsNoWorkerColumns,
} from './-hooks/useColumns';

const SearchSchema = createSearchSchema(SortableColumnKey);

const useAlertQuery = createAlertQueryHook<AlertRowWithUser>({
  category: 'NO_WORKER',
  queryKeySegment: 'no-worker',
  sortColumnMap: {
    [ColumnKey.appUser]: 'USER',
    [ColumnKey.app]: 'APP',
    user_actualRoleName: 'APP_USER_ROLE',
    user_statusName: 'APP_USER_STATUS',
    user_lastSignedInAt: 'LAST_SIGNED_IN_AT',
    user_createdAt: 'CREATED_AT',
  },
  filterAndMap: (items): AlertRowWithUser[] =>
    items
      .filter((item) => item.user)
      .map((item) => ({
        ...item,
        user: item.user as NonNullable<typeof item.user>,
      })),
});

const useExport = createAlertExportHook('NO_WORKER');

export const Route = createFileRoute(
  '/_staff/software/_layout/alerts/_alerts/no-worker/',
)({
  validateSearch: zodValidator(SearchSchema),
  component: SoftwareAlertsNoWorker,
});

function SoftwareAlertsNoWorker() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const counts = useLoaderData({ from: Route.parentRoute.id });
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.alert',
  });

  const { data, isPending } = useAlertQuery(search);
  const { mutate: onExport, isPending: exporting } = useExport(search);
  const columns = useSoftwareAlertsNoWorkerColumns();

  const onSearch = useCallback(
    (query: Partial<AlertSearchParams>) => {
      navigate({ search: (prev) => ({ ...prev, ...query }) as typeof prev });
    },
    [navigate],
  );

  return (
    <AlertPage
      alertTypeKey={SoftwareAlertType.noWorker.key}
      helpTitle={t('help.unidentified.title')}
      helpText={t('help.unidentified.text')}
      counts={counts}
      search={search}
      onSearch={onSearch}
      data={data}
      isPending={isPending}
      exporting={exporting}
      onExport={onExport}
      columns={columns}
    />
  );
}
